import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { BarChart3, LineChart as LineChartIcon } from 'lucide-react';

const MONTH_NAMES_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const formatSecondsToHours = (s) => {
  if (!s) return 0;
  return Math.round((s / 3600) * 100) / 100;
};

const METRICS = [
  { key: 'taskCount',         label: 'Görev Sayısı',  color: '#06b6d4', yAxis: 'left'  },
  { key: 'totalQuantity',     label: 'Toplam Adet',   color: '#8b5cf6', yAxis: 'left'  },
  { key: 'totalDurationHours', label: 'Toplam Süre',   color: '#f59e0b', yAxis: 'right' },
];

export default function MonthlyChart({ monthlyBreakdown = [], periodLabel, onMonthClick }) {
  const [chartType, setChartType] = useState('line');

  // ────────────────────────────────────────────────────────────────────────
  // CLICK HANDLER — Bir aya tıklandığında parent'a (Reports) ayın
  // başı ve sonu bildirilir; rapor o aralığa filtrelenir.
  // Geri almak için:
  //   1) Bu fonksiyonu silin VEYA
  //   2) Bar/Line öğelerinin `onClick={handlePointClick}` prop'unu kaldırın
  //   3) Reports.js'te <MonthlyChart ... onMonthClick={...}> prop'unu kaldırın
  // ────────────────────────────────────────────────────────────────────────
  const handlePointClick = (payload) => {
    if (!onMonthClick) return;
    // recharts: bar/line onClick → payload.activePayload[0].payload.month yerine
    // doğrudan payload (dot için) veya payload.activePayload (chart için) gelir.
    const month = payload?.month
      || payload?.activePayload?.[0]?.payload?.month
      || payload?.payload?.month;
    if (!month) return;
    onMonthClick(month);
  };

  const data = useMemo(() => {
    return (monthlyBreakdown || []).map((m) => {
      const [yyyy, mm] = m.month.split('-');
      const monthIdx = parseInt(mm, 10) - 1;
      const monthLabel = `${(MONTH_NAMES_TR[monthIdx] || mm).slice(0, 3)} ${yyyy}`;
      return {
        month: m.month,
        monthLabel,
        taskCount: m.taskCount || 0,
        totalQuantity: m.totalQuantity || 0,
        totalDurationSeconds: m.totalDurationSeconds || 0,
        totalDurationHours: formatSecondsToHours(m.totalDurationSeconds || 0),
        totalDurationFormatted: m.totalDuration || '00:00:00',
      };
    });
  }, [monthlyBreakdown]);

  const tooltipFormatter = (value, name, props) => {
    if (name === 'Toplam Süre') {
      const formatted = props?.payload?.totalDurationFormatted || `${value}sa`;
      return [formatted, 'Toplam Süre'];
    }
    if (name === 'Toplam Adet') return [`${value} adet`, 'Toplam Adet'];
    return [`${value} görev`, 'Görev Sayısı'];
  };

  if (!data.length) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" data-testid="monthly-chart-empty">
        <div>
          <h3 className="font-semibold text-gray-900">Aylık Üretim Dağılımı</h3>
          <p className="text-xs text-gray-500 mt-0.5">Filtrelere uygun veri bulunamadı</p>
        </div>
        <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
          Bu filtre için aylık döküm yok
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 mb-6" data-testid="monthly-chart">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Aylık Üretim Dağılımı</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {periodLabel || 'Filtrelenen veriler'} · {data.length} ay · 3 metrik birlikte
            {onMonthClick && <span className="ml-1 text-indigo-600">· Ayı tıklayarak filtrele</span>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Chart type toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
            <button
              type="button"
              onClick={() => setChartType('bar')}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${
                chartType === 'bar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Bar chart"
              data-testid="chart-type-bar"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setChartType('line')}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${
                chartType === 'line' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Line chart"
              data-testid="chart-type-line"
            >
              <LineChartIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 12, right: 24, left: 0, bottom: 8 }} barGap={0} barCategoryGap="20%" onClick={handlePointClick}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 12, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
              allowDecimals={false}
              label={{ value: 'Görev / Adet', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#94a3b8', offset: 12 }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: '#f59e0b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
              label={{ value: 'Saat', angle: 90, position: 'insideRight', fontSize: 11, fill: '#f59e0b', offset: 8 }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(6,182,212,0.05)' }}
              contentStyle={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 12,
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              }}
              formatter={tooltipFormatter}
              labelStyle={{ color: '#0f172a', fontWeight: 600 }}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              iconType="circle"
            />
            {chartType === 'bar' ? (
              <>
                <Bar yAxisId="left"  dataKey="taskCount"          name="Görev Sayısı" fill="#06b6d4" radius={[3, 3, 0, 0]} maxBarSize={32} cursor={onMonthClick ? 'pointer' : undefined} onClick={handlePointClick} />
                <Bar yAxisId="left"  dataKey="totalQuantity"      name="Toplam Adet"  fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={32} cursor={onMonthClick ? 'pointer' : undefined} onClick={handlePointClick} />
                <Bar yAxisId="right" dataKey="totalDurationHours" name="Toplam Süre"  fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={32} cursor={onMonthClick ? 'pointer' : undefined} onClick={handlePointClick} />
              </>
            ) : (
              <>
                <Line yAxisId="left"  type="monotone" dataKey="taskCount"          name="Görev Sayısı" stroke="#06b6d4" strokeWidth={2.5} dot={{ r: 4, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2, cursor: onMonthClick ? 'pointer' : undefined }} activeDot={{ r: 7, cursor: onMonthClick ? 'pointer' : undefined, onClick: (_, p) => handlePointClick(p) }} />
                <Line yAxisId="left"  type="monotone" dataKey="totalQuantity"      name="Toplam Adet"  stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 4, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2, cursor: onMonthClick ? 'pointer' : undefined }} activeDot={{ r: 7, cursor: onMonthClick ? 'pointer' : undefined, onClick: (_, p) => handlePointClick(p) }} />
                <Line yAxisId="right" type="monotone" dataKey="totalDurationHours" name="Toplam Süre"  stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2, cursor: onMonthClick ? 'pointer' : undefined }} activeDot={{ r: 7, cursor: onMonthClick ? 'pointer' : undefined, onClick: (_, p) => handlePointClick(p) }} />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
